/**
 * Centralized user-facing messages
 * This file contains all error, success, and loading messages used throughout the application.
 */

// Error messages
export const ERROR_MESSAGES = {
  // Generic errors
  GENERIC: 'An error occurred. Please try again.',
  UNAUTHORIZED: 'You are not authorized to perform this action.',
  NOT_FOUND: (resource: string) => `${resource} not found`,

  // CRUD operation errors
  FETCH_FAILED: (resource: string) => `Failed to load ${resource}`,
  SAVE_FAILED: (resource: string) => `Failed to save ${resource}`,
  DELETE_FAILED: (resource: string) => `Failed to delete ${resource}`,
  UPDATE_FAILED: (resource: string) => `Failed to update ${resource}`,
  CREATE_FAILED: (resource: string) => `Failed to create ${resource}`,

  // Specific operation errors
  STATUS_UPDATE_FAILED: 'Failed to update status',
  ASSET_ASSIGN_FAILED: 'Failed to assign asset',
  REASSIGN_FAILED: 'Failed to reassign task',
  REMARK_ADD_FAILED: 'Failed to add remark',
  REMINDER_FAILED: 'Failed to send reminder',
  APPROVAL_FAILED: 'Failed to process approval',
  DECLINE_FAILED: 'Failed to decline request',
  IMPORT_FAILED: 'Failed to import data',
  EXPORT_FAILED: 'Failed to export data',

  // Validation errors
  REQUIRED_FIELD: (field: string) => `${field} is required`,
  INVALID_EMAIL: 'Please enter a valid email address',
  INVALID_DATE: 'Please enter a valid date',
} as const;

// Success messages
export const SUCCESS_MESSAGES = {
  // Generic success
  SAVED: (resource: string) => `${resource} saved successfully`,
  DELETED: (resource: string) => `${resource} deleted successfully`,
  UPDATED: (resource: string) => `${resource} updated successfully`,
  CREATED: (resource: string) => `${resource} created successfully`,

  // Specific operation success
  STATUS_UPDATED: 'Status updated successfully',
  ASSET_ASSIGNED: 'Asset assigned successfully',
  TASK_REASSIGNED: 'Task reassigned successfully',
  REMARK_ADDED: 'Remark added successfully',
  REMINDER_SENT: 'Reminder sent successfully',
  REMINDERS_SENT: (count: number) => `${count} reminder(s) sent successfully`,
  APPROVAL_PROCESSED: 'Approval processed successfully',
  REQUEST_APPROVED: 'Request approved successfully',
  REQUEST_REJECTED: 'Request rejected successfully',
  REQUEST_SUBMITTED: 'Request submitted successfully',
  IMPORT_SUCCESS: 'Import completed successfully',
  TASK_ADDED: 'Task added successfully',
  POST_ONBOARDING_TASK_ADDED: 'Post-onboarding task added successfully',

  // Onboarding/Offboarding specific
  ONBOARDING_DECLINED: 'Onboarding declined. Offboarding draft created.',
} as const;

// Loading states
export const LOADING_TEXT = {
  DEFAULT: 'Loading...',
  SAVING: 'Saving...',
  DELETING: 'Deleting...',
  UPDATING: 'Updating...',
  SUBMITTING: 'Submitting...',
  PROCESSING: 'Processing...',
  LOADING_DATA: 'Loading data...',
  LOADING_DETAILS: 'Loading details...',
  SENDING: 'Sending...',
  DECLINING: 'Declining...',
} as const;

// Empty state messages
export const EMPTY_STATE_MESSAGES = {
  NO_DATA: 'No data available',
  NO_RESULTS: 'No results found',
  NO_TASKS: 'No tasks assigned yet',
  NO_EMPLOYEES: (type: 'active' | 'exited') =>
    type === 'active' ? 'No active employees in the system.' : 'Exited employees will appear here after offboarding.',
  NO_REQUESTS: 'No requests found',
  SEARCH_NO_RESULTS: 'Try adjusting your search criteria.',
} as const;

// Confirmation messages
export const CONFIRM_MESSAGES = {
  DELETE: (resource: string) => `Are you sure you want to delete this ${resource}?`,
  COMPLETE_TASK: 'Are you sure you want to mark this task as completed?',
  SEND_REMINDER: (taskName: string) => `This will send an email to the assignee for task "${taskName}". Are you sure you want to send a reminder?`,
  SEND_ALL_REMINDERS: 'This will send an email to all assignees with pending tasks. Are you sure you want to remind all?',
  DECLINE_ONBOARDING: (employeeName: string) => `Are you sure you want to decline this onboarding request for ${employeeName}?`,
  APPROVE_REQUEST: 'Are you sure you want to approve this request?',
  REJECT_REQUEST: 'Are you sure you want to reject this request?',
} as const;
